/**
 * MANAGEMENT (14) · ParamTypes — the screen-owned WRITE layer (publish / reissue / retire / restore),
 * ADDITIVE to ../api.ts (the shell ships the `useParamtypes` READ hook + the `managementKeys` factory +
 * the `isManagementAbsent` helper; this file adds the four mutations following the shell's documented
 * write convention verbatim). No shared-file edit — it imports the shell data layer and re-exports the
 * pieces the screen needs from ONE module.
 *
 * THE WRITE CONVENTION (from ../api.ts header): every mutation is
 *   `useApiMutation((vars) => api.<verb>("/management/paramtypes...", vars), { invalidate: [...] })`
 * and invalidates the affected platform key (here the whole paramtypes namespace — a publish/retire shifts
 * the list, the facets, AND the Overview count, so we invalidate the section root, not just one filter key).
 *
 * Endpoints (all on the `platformRoute` chain, capability `management.paramtype.manage`, NO tenant scope):
 *   POST   /management/paramtypes                 → publish a NEW type (create)
 *   PUT    /management/paramtypes/:code           → REISSUE the same code (correct by reissue, never write-once)
 *   DELETE /management/paramtypes/:code           → RETIRE (supersede; statut → retire, row survives)
 *   POST   /management/paramtypes/:code/restaurer → reinstate a retired type (statut → actif)
 */
import { api, useApiMutation } from "../api";
import type { ParamtypeDetail, CreateParamtypeBody, UpdateParamtypeBody } from "./paramtypes.types";

/** The platform key every paramtypes mutation invalidates — the whole section (list + facets + Overview KPI). */
const PARAMTYPES_KEY = ["platform", "management", "paramtypes"] as const;

/** Encode a taxonomy code for the URL path (codes are alnum + `._-`, but encode defensively all the same). */
function codePath(code: string): string {
  return encodeURIComponent(code);
}

/** POST /management/paramtypes — publish a NEW global type. A code collision surfaces as a typed CONFLICT. */
export function useCreateParamtype() {
  return useApiMutation<ParamtypeDetail, CreateParamtypeBody>(
    (body) => api.post<ParamtypeDetail>("/management/paramtypes", body),
    { invalidate: [PARAMTYPES_KEY] },
  );
}

/** PUT /management/paramtypes/:code — REISSUE the same code (partial patch; the server bumps the version). */
export function useReissueParamtype() {
  return useApiMutation<ParamtypeDetail, { code: string; body: UpdateParamtypeBody }>(
    ({ code, body }) => api.put<ParamtypeDetail>(`/management/paramtypes/${codePath(code)}`, body),
    { invalidate: [PARAMTYPES_KEY] },
  );
}

/** DELETE /management/paramtypes/:code — RETIRE (supersede): statut → retire. Idempotent server-side. */
export function useRetireParamtype() {
  return useApiMutation<ParamtypeDetail, { code: string }>(
    ({ code }) => api.delete<ParamtypeDetail>(`/management/paramtypes/${codePath(code)}`),
    { invalidate: [PARAMTYPES_KEY] },
  );
}

/** POST /management/paramtypes/:code/restaurer — reinstate a retired type (statut → actif). Idempotent. */
export function useRestoreParamtype() {
  return useApiMutation<ParamtypeDetail, { code: string }>(
    ({ code }) => api.post<ParamtypeDetail>(`/management/paramtypes/${codePath(code)}/restaurer`, {}),
    { invalidate: [PARAMTYPES_KEY] },
  );
}

// Re-export the shell read hook + the absent-module helper + the key factory so the screen wires its whole
// data layer from this ONE module (the shell's documented "section agent imports from here" convention).
export { useParamtypes, isManagementAbsent, managementKeys } from "../api";
